import { useEffect, useState } from "react";
import i18n from "locales/i18n";
import {
  SetVendorAvailabilityInput,
  VendorAvailabilityFragment,
  useGetVendorAvailabilityQuery,
  useSetVendorAvailabilityMutation,
} from "generated/graphql";
import {
  AvailabilityData,
  BookingHorizon,
  BufferMinutes,
  CustomEntry,
  DayHours,
  RangeAvailability,
  SingleAvailability,
} from "./types";

const stripDay = (d: {
  available: boolean;
  from: string;
  to: string;
}): DayHours => ({ available: d.available, from: d.from, to: d.to });

// ---- GraphQL -> frontend AvailabilityData ----

const tc = i18n.getFixedT(null, "calendar");
export const toAvailabilityData = (
  a: VendorAvailabilityFragment,
): AvailabilityData => ({
  weekly: {
    mon: stripDay(a.weekly.mon),
    tue: stripDay(a.weekly.tue),
    wed: stripDay(a.weekly.wed),
    thu: stripDay(a.weekly.thu),
    fri: stripDay(a.weekly.fri),
    sat: stripDay(a.weekly.sat),
    sun: stripDay(a.weekly.sun),
  },
  customDates: a.customDates.map(
    (c): CustomEntry =>
      c.mode === "single"
        ? {
            id: c.id,
            mode: "single",
            date: c.date ?? "",
            availability: c.availability as SingleAvailability,
            from: c.from ?? undefined,
            to: c.to ?? undefined,
            note: c.note ?? undefined,
          }
        : {
            id: c.id,
            mode: "range",
            startDate: c.startDate ?? "",
            endDate: c.endDate ?? "",
            availability: c.availability as RangeAvailability,
            from: c.from ?? undefined,
            to: c.to ?? undefined,
            perDay:
              c.perDay?.map((p) => ({
                date: p.date,
                available: p.available,
                from: p.from,
                to: p.to,
              })) ?? undefined,
            note: c.note ?? undefined,
          },
  ),
  bufferMinutes: a.bufferMinutes as BufferMinutes,
  bookingHorizon: a.bookingHorizon as BookingHorizon,
  allowSameDayBookings: a.allowSameDayBookings,
});

// ---- frontend AvailabilityData -> GraphQL input ----
export const toInput = (
  data: AvailabilityData,
): SetVendorAvailabilityInput => ({
  weekly: {
    mon: stripDay(data.weekly.mon),
    tue: stripDay(data.weekly.tue),
    wed: stripDay(data.weekly.wed),
    thu: stripDay(data.weekly.thu),
    fri: stripDay(data.weekly.fri),
    sat: stripDay(data.weekly.sat),
    sun: stripDay(data.weekly.sun),
  },
  customDates: data.customDates.map((e) =>
    e.mode === "single"
      ? {
          id: e.id,
          mode: "single",
          date: e.date,
          availability: e.availability,
          from: e.from,
          to: e.to,
          note: e.note,
        }
      : {
          id: e.id,
          mode: "range",
          startDate: e.startDate,
          endDate: e.endDate,
          availability: e.availability,
          from: e.from,
          to: e.to,
          perDay: e.perDay?.map((p) => ({
            date: p.date,
            available: p.available,
            from: p.from,
            to: p.to,
          })),
          note: e.note,
        },
  ),
  bufferMinutes: data.bufferMinutes,
  bookingHorizon: data.bookingHorizon,
  allowSameDayBookings: data.allowSameDayBookings,
});

export interface SaveResult {
  ok: boolean;
  error?: string;
}

/**
 * Loads the logged-in vendor's availability and exposes a save mutation.
 * `availability` is `undefined` while loading, `null` when the vendor has not
 * configured it yet (calendar should be locked), or the saved data.
 */
export const useVendorAvailability = (isVendor: boolean) => {
  const [{ data, fetching }] = useGetVendorAvailabilityQuery({
    pause: !isVendor,
    requestPolicy: "cache-and-network",
  });
  const [, setVendorAvailability] = useSetVendorAvailabilityMutation();

  const [availability, setAvailability] = useState<
    AvailabilityData | null | undefined
  >(undefined);

  useEffect(() => {
    if (!isVendor) {
      setAvailability(null);
      return;
    }
    if (!fetching) {
      setAvailability(
        data?.vendorAvailability
          ? toAvailabilityData(data.vendorAvailability)
          : null,
      );
    }
  }, [isVendor, fetching, data]);

  const save = async (next: AvailabilityData): Promise<SaveResult> => {
    const res = await setVendorAvailability({ input: toInput(next) });
    const payload = res.data?.setVendorAvailability;
    if (res.error || (payload?.errors && payload.errors.length > 0)) {
      return {
        ok: false,
        error:
          payload?.errors?.[0]?.message ??
          res.error?.message ??
          tc("snack.save_availability_failed"),
      };
    }
    setAvailability(next);
    return { ok: true };
  };

  return { availability, loading: availability === undefined, save };
};
